import { DownOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { ApolloError, useApolloClient } from '@apollo/client';
import { Button, Dropdown, Menu, message, Modal, Space } from 'antd';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type NamirialSigningModuleSpecsFragment } from '../../../../api/fragments/NamirialSigningModuleSpecs';
import {
    DeleteModuleDocument,
    type DeleteModuleMutation,
    type DeleteModuleMutationVariables,
} from '../../../../api/mutations/deleteModule';
import { type PermissionValues } from '../../../../utilities/hasPermissions';
import { useUpdateNamirialDrawer } from './UpdateNamirialDrawer';

export type NamirialSigningModuleActionsProps = {
    module: NamirialSigningModuleSpecsFragment;
    permissions: Pick<PermissionValues, 'hasDeletePermission' | 'hasUpdatePermission'>;
};

const NamirialSigningModuleActions = ({ module, permissions }: NamirialSigningModuleActionsProps) => {
    const { t } = useTranslation(['namirialSigningModuleDetails', 'moduleDetails']);
    const apolloClient = useApolloClient();
    const navigate = useNavigate();

    const updateNamirialDrawer = useUpdateNamirialDrawer(module);
    const { hasUpdatePermission, hasDeletePermission } = permissions;

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('moduleDetails:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('moduleDetails:deleteModal.content'),
            okText: t('moduleDetails:deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('moduleDetails:deleteModal.cancelButton'),
            onOk: async () => {
                try {
                    message.loading({
                        content: t('moduleDetails:deleteModal.messages.submitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    await apolloClient
                        .mutate<DeleteModuleMutation, DeleteModuleMutationVariables>({
                            mutation: DeleteModuleDocument,
                            variables: { moduleId: module.id },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    message.success({
                        content: t('moduleDetails:deleteModal.messages.success'),
                        key: 'primary',
                    });

                    navigate('/admin/system/modules');
                } catch (error) {
                    if (error instanceof ApolloError) {
                        message.error(error.graphQLErrors[0].message);
                    }
                }
            },
        });
    }, [apolloClient, module, navigate, t]);

    const items = useMemo(
        () =>
            [
                hasUpdatePermission && {
                    label: t('namirialSigningModuleDetails:mainActionButtons.updateSpecs'),
                    key: 'updateSpecs',
                    icon: <EditOutlined />,
                    onClick: updateNamirialDrawer.open,
                },
                hasDeletePermission && {
                    label: t('namirialSigningModuleDetails:mainActionButtons.delete'),
                    key: 'delete',
                    icon: <DeleteOutlined />,
                    onClick: deleteAction,
                    danger: true,
                },
            ].filter(Boolean),
        [hasUpdatePermission, t, updateNamirialDrawer.open, hasDeletePermission, deleteAction]
    );

    if (!items.length) {
        return null;
    }

    return (
        <>
            <Dropdown overlay={<Menu items={items} />}>
                <Button type="primary">
                    <Space>
                        <span>{t('namirialSigningModuleDetails:mainActionButtons.actions')}</span>
                        <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
            {updateNamirialDrawer.render()}
        </>
    );
};

export default NamirialSigningModuleActions;
